import { colors, Icon, radii, Text, type IconName } from '@khaddi/ui';
import type { ReactNode } from 'react';
import { Pressable, View, type StyleProp, type ViewStyle } from 'react-native';

/** White rounded section used down the checkout and loyalty screens. */
export const Section = ({ children, style }: { children: ReactNode; style?: StyleProp<ViewStyle> }) => (
  <View style={[{ backgroundColor: colors.surface, borderRadius: radii.lg, padding: 14 }, style]}>{children}</View>
);

/** Square with the gateway's colour and abbreviation, as in the payment list. */
export const PaymentLogo = ({ abbr, color }: { abbr: string; color: string }) => (
  <View style={{ width: 32, height: 32, borderRadius: 9, backgroundColor: color, alignItems: 'center', justifyContent: 'center' }}>
    <Text variant="label" color="#FFFFFF" style={{ fontSize: 9, fontWeight: '800' }}>
      {abbr}
    </Text>
  </View>
);

/** Rounded option button; `fill` paints it solid when picked (sugar, ice, areas), otherwise a pale tint (size, shift). */
export const OptionPill = ({
  label,
  sub,
  selected,
  onPress,
  fill = true,
  style,
  height = 44,
}: {
  label: string;
  sub?: string;
  selected: boolean;
  onPress: () => void;
  fill?: boolean;
  style?: StyleProp<ViewStyle>;
  height?: number;
}) => {
  const background = selected ? (fill ? colors.primary : colors.surfaceMuted) : colors.surface;
  const foreground = selected && fill ? colors.onPrimary : colors.text;
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ selected }}
      aria-pressed={selected}
      onPress={onPress}
      style={[
        { height, minWidth: 44, borderRadius: height / 2, borderWidth: 2, borderColor: selected ? colors.primaryText : colors.border, backgroundColor: background, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 10 },
        style,
      ]}
    >
      <Text variant="bodyStrong" color={foreground} style={{ fontSize: 13 }}>
        {label}
      </Text>
      {sub ? (
        <Text variant="label" color={colors.textMuted} style={{ fontWeight: '500' }}>
          {sub}
        </Text>
      ) : null}
    </Pressable>
  );
};

/** Leading icon + title/subtitle + trailing control: the toggle rows and account menu. */
export const ListRow = ({
  icon,
  iconColor = colors.primaryText,
  title,
  subtitle,
  trailing,
  onPress,
  last = false,
  danger = false,
}: {
  icon: IconName;
  iconColor?: string;
  title: string;
  subtitle?: string;
  trailing?: ReactNode;
  onPress?: () => void;
  last?: boolean;
  danger?: boolean;
}) => {
  const content = (
    <>
      <Icon name={icon} size={22} color={danger ? colors.danger : iconColor} filled={icon === 'star'} />
      <View style={{ flex: 1 }}>
        <Text variant="body" color={danger ? colors.danger : colors.text} style={{ fontWeight: '600' }}>
          {title}
        </Text>
        {subtitle ? (
          <Text variant="label" style={{ fontWeight: '400' }}>
            {subtitle}
          </Text>
        ) : null}
      </View>
      {trailing}
    </>
  );
  const style: ViewStyle = { flexDirection: 'row', alignItems: 'center', gap: 12, minHeight: 56, borderBottomWidth: last ? 0 : 1, borderBottomColor: colors.divider };
  if (!onPress) return <View style={style}>{content}</View>;
  return (
    <Pressable accessibilityRole="button" onPress={onPress} style={style}>
      {content}
    </Pressable>
  );
};

/** Inline red message under a form or button. */
export const InlineError = ({ message }: { message: string | null | undefined }) =>
  message ? (
    <Text variant="caption" color={colors.danger} style={{ fontWeight: '600' }}>
      {message}
    </Text>
  ) : null;

/** "9:05" for a countdown in seconds. */
export const formatCountdown = (seconds: number) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

/**
 * A short-lived code the customer shows the cashier, with its countdown: phone verification and
 * counter redemption share the same gold box so the cashier recognises it.
 */
export const ShownCode = ({ code, secondsLeft, size = 'md', dimmed = false, note }: { code: string; secondsLeft: number; size?: 'md' | 'lg'; dimmed?: boolean; note?: string }) => {
  const large = size === 'lg';
  const tint = dimmed ? colors.textSubtle : colors.goldText;
  return (
    <View
      accessible
      accessibilityLabel={`Mã ${code.split('').join(' ')}. ${note ?? `Hết hạn sau ${Math.floor(secondsLeft / 60)} phút ${secondsLeft % 60} giây`}`}
      style={{ backgroundColor: dimmed ? colors.surfaceMuted : colors.goldSoft, borderRadius: 14, padding: large ? 18 : 14, alignItems: 'center', gap: 4 }}
    >
      <Text
        variant="display"
        numeric
        color={tint}
        style={[{ letterSpacing: large ? 10 : 8, paddingLeft: large ? 10 : 8 }, large ? { fontSize: 48, lineHeight: 56 } : null, dimmed ? { textDecorationLine: 'line-through' } : null]}
      >
        {code}
      </Text>
      <Text variant="caption" numeric color={tint}>
        {note ?? `Hết hạn sau ${formatCountdown(secondsLeft)}`}
      </Text>
    </View>
  );
};
